<template>
    <div class="communications-inbox">
        <v-card class="inbox-root-card">
            <v-card-title class="inbox-header white--text px-3 pb-3">
                <v-icon color="white" class="mr-2">mdi-inbox</v-icon>
                Communications Inbox
                <v-spacer></v-spacer>
                <span class="inbox-live caption mr-3">
                    <span class="inbox-live-dot"></span>
                    Live
                </span>
                <v-btn
                    outline
                    color="white"
                    small
                    class="inbox-refresh-btn"
                    :loading="loading"
                    @click="loadInbox"
                >
                    <v-icon small class="mr-1">mdi-refresh</v-icon>
                    Refresh
                </v-btn>
            </v-card-title>

            <div class="inbox-canvas">
                <v-alert
                    :value="!!errorMessage"
                    type="error"
                    @input="errorMessage = ''"
                >
                    {{ errorMessage }}
                </v-alert>

                <v-layout row wrap align-center class="inbox-chip-row inbox-panel">
                    <v-chip
                        small
                        text-color="white"
                        :color="filters.assignedFilter === 'mineAndUnassigned' ? '#4caf50' : '#b0bec5'"
                        class="inbox-chip"
                        @click.native="setAssignedFilter('mineAndUnassigned')"
                    >
                        My Queue ({{ counts.all }})
                    </v-chip>
                    <v-chip
                        small
                        text-color="white"
                        :color="filters.assignedFilter === 'mine' ? '#4caf50' : '#b0bec5'"
                        class="inbox-chip"
                        @click.native="setAssignedFilter('mine')"
                    >
                        Assigned to Me ({{ counts.assignedToMe }})
                    </v-chip>
                    <v-chip
                        small
                        text-color="white"
                        :color="filters.assignedFilter === 'unassigned' ? '#fdb400' : '#b0bec5'"
                        class="inbox-chip"
                        @click.native="setAssignedFilter('unassigned')"
                    >
                        Unassigned ({{ counts.unassigned }})
                    </v-chip>
                    <v-chip
                        small
                        text-color="white"
                        :color="onlyNeedsAccount ? '#e53935' : '#b0bec5'"
                        class="inbox-chip"
                        @click.native="onlyNeedsAccount = !onlyNeedsAccount"
                    >
                        Unknown account ({{ counts.needsAccount }})
                    </v-chip>
                    <v-divider vertical class="mx-2"></v-divider>
                    <v-chip
                        v-for="typeOption in typeOptions"
                        :key="typeOption.value"
                        small
                        text-color="white"
                        :color="filters.types.includes(typeOption.value) ? typeColor(typeOption.value) : '#cfd8dc'"
                        class="inbox-chip"
                        @click.native="toggleType(typeOption.value)"
                    >
                        {{ typeOption.label }} ({{ counts[typeOption.countKey] }})
                    </v-chip>
                </v-layout>

                <v-card outline class="inbox-filter-bar pa-2">
                    <div class="inbox-filter-toolbar">
                        <div class="inbox-filter-field inbox-filter-field--search">
                            <v-text-field
                                v-model="filters.search"
                                placeholder="Search name, account, phone, subject"
                                prepend-inner-icon="mdi-magnify"
                                solo
                                flat
                                dense
                                hide-details
                                clearable
                                background-color="#fff"
                            ></v-text-field>
                        </div>
                        <div class="inbox-filter-field inbox-filter-field--assigned">
                            <v-select
                                v-model="filters.assignedFilter"
                                :items="visibleAssignedFilterOptions"
                                prepend-inner-icon="mdi-account-check"
                                solo
                                flat
                                dense
                                hide-details
                                background-color="#fff"
                                @change="loadInbox"
                            ></v-select>
                        </div>
                        <div v-if="filters.assignedFilter === 'user'" class="inbox-filter-field">
                            <v-autocomplete
                                v-model="filters.assignedToUser"
                                placeholder="User..."
                                prepend-inner-icon="mdi-account"
                                solo
                                flat
                                dense
                                hide-details
                                hide-no-data
                                :items="users"
                                :search-input.sync="loadUsers"
                                :menu-props="userMenuProps"
                                background-color="#fff"
                                @focus="ensureUsersLoaded"
                                @change="loadInbox"
                            ></v-autocomplete>
                        </div>
                        <div class="inbox-filter-field">
                            <v-select
                                v-model="filters.status"
                                :items="statusOptions"
                                prepend-inner-icon="mdi-flag-outline"
                                solo
                                flat
                                dense
                                hide-details
                                background-color="#fff"
                                @change="loadInbox"
                            ></v-select>
                        </div>
                        <div class="inbox-filter-field inbox-filter-field--daterange">
                            <v-menu
                                v-model="fromDateMenu"
                                :close-on-content-click="true"
                                offset-y
                            >
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        v-model="filters.fromDate"
                                        placeholder="From"
                                        prepend-inner-icon="mdi-calendar"
                                        readonly
                                        solo
                                        flat
                                        dense
                                        hide-details
                                        clearable
                                        background-color="#fff"
                                        v-on="on"
                                        @click:clear="filters.fromDate = ''; loadInbox()"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="filters.fromDate"
                                    @input="fromDateMenu = false; loadInbox()"
                                ></v-date-picker>
                            </v-menu>
                            <span class="inbox-filter-daterange-sep">&ndash;</span>
                            <v-menu
                                v-model="toDateMenu"
                                :close-on-content-click="true"
                                offset-y
                            >
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        v-model="filters.toDate"
                                        placeholder="To"
                                        prepend-inner-icon="mdi-calendar"
                                        readonly
                                        solo
                                        flat
                                        dense
                                        hide-details
                                        clearable
                                        background-color="#fff"
                                        v-on="on"
                                        @click:clear="filters.toDate = ''; loadInbox()"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="filters.toDate"
                                    @input="toDateMenu = false; loadInbox()"
                                ></v-date-picker>
                            </v-menu>
                        </div>
                        <div class="inbox-filter-field">
                            <v-select
                                v-model="sortBy"
                                :items="sortOptions"
                                prepend-inner-icon="mdi-sort"
                                solo
                                flat
                                dense
                                hide-details
                                background-color="#fff"
                            ></v-select>
                        </div>
                    </div>
                </v-card>

                <v-progress-linear
                    v-if="loading"
                    :indeterminate="true"
                    color="success"
                    class="mb-2"
                ></v-progress-linear>

                <v-layout row wrap class="inbox-split">
                    <v-flex xs12 md5 class="inbox-split-list">
                        <v-layout
                            v-if="selectedIds.length"
                            row
                            align-center
                            class="inbox-bulk-bar mb-2"
                        >
                            <span class="inbox-bulk-count">
                                {{ selectedIds.length }} selected
                            </span>
                            <v-btn
                                small
                                color="success"
                                :loading="bulkProcessing"
                                @click="bulkPickup"
                            >
                                Pickup Selected
                            </v-btn>
                            <v-btn
                                small
                                color="success"
                                :loading="bulkProcessing"
                                @click="bulkUnassign"
                            >
                                Unassign Selected
                            </v-btn>
                            <v-btn
                                small
                                color="success"
                                :loading="bulkProcessing"
                                @click="openBulkUpdateDialog"
                            >
                                Update Selected
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn small flat @click="clearSelection">Clear</v-btn>
                        </v-layout>
                        <div class="inbox-list-card">
                            <div class="inbox-row-grid inbox-row-grid--header">
                                <div class="inbox-row-checkbox">
                                    <v-checkbox
                                        :input-value="allFilteredSelected"
                                        :indeterminate="selectedIds.length > 0 && !allFilteredSelected"
                                        color="success"
                                        hide-details
                                        @change="toggleSelectAll"
                                    ></v-checkbox>
                                </div>
                                <div>Type</div>
                                <div>Account</div>
                                <div>Assigned</div>
                                <div>30d</div>
                                <div>Time</div>
                            </div>
                            <div class="inbox-list">
                                <div
                                    v-if="!loading && !filteredItems.length"
                                    class="grey--text text-xs-center py-4"
                                >
                                    No communications match your filters
                                </div>
                                <v-card
                                    v-for="item in filteredItems"
                                    :key="item.communicationID"
                                    class="inbox-item"
                                    :class="{
                                        'inbox-item--active':
                                            selected &&
                                            selected.communicationID ===
                                                item.communicationID,
                                    }"
                                    @click.native="selectItem(item)"
                                >
                                    <div class="inbox-row-grid">
                                        <div class="inbox-row-checkbox" @click.stop>
                                            <v-checkbox
                                                :input-value="isRowSelected(item)"
                                                color="success"
                                                hide-details
                                                @change="toggleRowSelection(item)"
                                            ></v-checkbox>
                                        </div>
                                        <div class="inbox-row-type">
                                            <v-icon small :color="typeColor(item.type)">
                                                {{ typeIcon(item.type) }}
                                            </v-icon>
                                        </div>
                                        <div class="inbox-row-account">
                                            <div class="inbox-row-name">
                                                <span class="inbox-row-name-en">
                                                    {{ item.name || item.accountID || "Unknown account" }}
                                                </span>
                                                <span v-if="item.nameH" class="inbox-row-name-he">
                                                    {{ item.nameH }}
                                                </span>
                                                <span
                                                    v-if="item.attachmentCount"
                                                    class="inbox-row-attach"
                                                    :title="item.attachmentCount + ' attachment(s)'"
                                                >
                                                    <v-icon small color="#607d8b">mdi-paperclip</v-icon>
                                                    {{ item.attachmentCount }}
                                                </span>
                                            </div>
                                            <div class="inbox-row-preview">
                                                {{ previewText(item) }}
                                            </div>
                                        </div>
                                        <div class="inbox-row-assigned">
                                            <v-chip
                                                small
                                                text-color="white"
                                                :color="assignedChipColor(item.assignedTo)"
                                            >
                                                {{ assignedChipLabel(item.assignedTo) }}
                                            </v-chip>
                                            <v-chip
                                                v-if="item.needsAccount"
                                                small
                                                text-color="white"
                                                color="#e53935"
                                                class="mt-1"
                                            >
                                                Unknown account
                                            </v-chip>
                                        </div>
                                        <div class="inbox-row-count">
                                            <button
                                                v-if="item.communicationCount !== null && item.communicationCount !== undefined"
                                                type="button"
                                                class="inbox-comm-count inbox-comm-count--grid"
                                                title="Communications from this account in the last 30 days"
                                                @click.stop="openCommunicationHistory(item)"
                                            >
                                                {{ item.communicationCount }}
                                            </button>
                                        </div>
                                        <div class="inbox-row-time">
                                            <span class="inbox-row-date">{{ formatListDate(item.date) }}</span>
                                            <span class="inbox-row-clock">{{ formatListTime(item.date) }}</span>
                                        </div>
                                    </div>
                                </v-card>
                            </div>
                        </div>
                    </v-flex>
                    <v-flex xs12 md7 class="inbox-split-detail">
                        <v-card v-if="!selected" class="inbox-detail-empty pa-4 grey--text text-xs-center">
                            Select a communication to review.
                        </v-card>
                        <v-card v-else class="inbox-detail">
                            <div class="inbox-detail-sticky">
                                <div
                                    class="inbox-account-box"
                                    :class="{ 'inbox-account-box--missing': selected.needsAccount }"
                                >
                                    <div v-if="!selected.needsAccount">
                                        <div class="inbox-account-name">
                                            <v-icon
                                                small
                                                class="mr-2"
                                                :color="typeColor(selected.type)"
                                            >
                                                {{ typeIcon(selected.type) }}
                                            </v-icon>
                                            <span class="inbox-account-name-en">
                                                {{ selected.name || selected.accountID }}
                                            </span>
                                            <span v-if="selected.nameH" class="inbox-account-name-he">
                                                {{ selected.nameH }}
                                            </span>
                                        </div>
                                        <div class="inbox-account-meta">
                                            Account {{ selected.accountID }}
                                            <span v-if="selected.emailAddress"> &middot; {{ selected.emailAddress }}</span>
                                        </div>
                                        <div v-if="selected.address" class="inbox-account-meta">
                                            <v-icon small color="#b0bec5" class="mr-1">mdi-map-marker</v-icon>
                                            {{ selected.address }}
                                        </div>
                                    </div>
                                    <div v-else class="inbox-account-missing-text">
                                        <v-icon small color="#ffcc80" class="mr-1">mdi-account-alert</v-icon>
                                        Unknown account
                                    </div>
                                </div>

                                <div v-if="selected.needsAccount" class="inbox-link-box">
                                    <div class="inbox-link-title">
                                        <v-icon small color="#e65100" class="mr-1">mdi-alert-circle</v-icon>
                                        Link to account
                                    </div>
                                    <div class="inbox-link-row">
                                        <assign-account-lookup
                                            ref="assignLookup"
                                            v-model="assignAccountID"
                                            class="inbox-link-lookup"
                                        ></assign-account-lookup>
                                        <v-btn
                                            color="success"
                                            small
                                            :loading="processing"
                                            :disabled="!assignAccountID"
                                            @click="assignAccount"
                                        >
                                            <v-icon small class="mr-1">mdi-link-variant</v-icon>
                                            Link account
                                        </v-btn>
                                    </div>
                                </div>

                                <div
                                    v-if="!selected.needsAccount"
                                    class="inbox-reg-box"
                                >
                                    <div class="inbox-reg-header">
                                        <div class="inbox-reg-title">
                                            <v-icon small color="#2e7d32" class="mr-1">mdi-clipboard-text</v-icon>
                                            Registration
                                        </div>
                                        <v-chip
                                            v-if="selected.registrationStatus"
                                            small
                                            text-color="white"
                                            :color="regStatusColor(selected.registrationStatus)"
                                        >
                                            <v-icon small color="white" class="mr-1">
                                                {{ regStatusIcon(selected.registrationStatus) }}
                                            </v-icon>
                                            {{ selected.registrationStatus }}
                                        </v-chip>
                                        <v-spacer></v-spacer>
                                        <button
                                            v-if="selected.communicationCount !== null && selected.communicationCount !== undefined"
                                            type="button"
                                            class="inbox-comm-count inbox-comm-count--link"
                                            title="View communications from this account in the last 30 days"
                                            @click.stop="openCommunicationHistory(selected)"
                                        >
                                            <v-icon x-small color="#2e7d32">mdi-history</v-icon>
                                            <span class="inbox-comm-count-num">{{ selected.communicationCount }}</span>
                                            <span>{{ selected.communicationCount === 1 ? "communication" : "communications" }}</span>
                                            <span class="inbox-comm-count-period">30d</span>
                                        </button>
                                    </div>

                                    <div class="inbox-reg-students">
                                        <div v-if="accountStudentsLoading" class="inbox-reg-loading">
                                            <v-progress-circular indeterminate size="14" width="2" color="success" class="mr-2"></v-progress-circular>
                                            Loading students...
                                        </div>
                                        <template v-else-if="accountStudents.length">
                                            <div
                                                v-for="student in accountStudents"
                                                :key="student.studentID"
                                                class="inbox-student-chip"
                                                :class="{
                                                    'inbox-student-chip--warning':
                                                        !student.studentRegistered && isPartiallyRegistered,
                                                }"
                                                :title="
                                                    student.studentRegistered
                                                        ? 'Registered'
                                                        : 'Not registered'
                                                "
                                            >
                                                <v-icon
                                                    x-small
                                                    :color="
                                                        student.studentRegistered
                                                            ? '#4caf50'
                                                            : (isPartiallyRegistered ? '#e53935' : '#b0bec5')
                                                    "
                                                    class="mr-1"
                                                    :title="
                                                        student.studentRegistered
                                                            ? 'Registered'
                                                            : 'Not registered'
                                                    "
                                                >
                                                    {{
                                                        student.studentRegistered
                                                            ? "mdi-check-circle"
                                                            : (isPartiallyRegistered ? "mdi-alert-circle" : "mdi-circle-outline")
                                                    }}
                                                </v-icon>
                                                {{ student.engName || student.hebName }}
                                                <span v-if="student.class" class="inbox-student-class">&middot; {{ student.class }}</span>
                                            </div>
                                        </template>
                                        <span v-else class="inbox-reg-value">No students on file</span>
                                    </div>

                                    <div v-if="incompleteRequirements.length" class="inbox-reg-missing">
                                        <div class="inbox-reg-label mb-1">Missing ({{ incompleteRequirements.length }})</div>
                                        <div class="inbox-reg-missing-list">
                                            <div
                                                v-for="(req, index) in incompleteRequirements"
                                                :key="index"
                                                class="inbox-reg-missing-item"
                                            >
                                                <div class="inbox-reg-missing-title">
                                                    <span
                                                        v-if="requirementStudentName(req)"
                                                        class="inbox-reg-missing-student"
                                                    >{{ requirementStudentName(req) }}:</span>
                                                    {{ req.reqType }}
                                                </div>
                                                <div
                                                    v-if="requirementExtraInfo(req)"
                                                    class="inbox-reg-missing-desc"
                                                >
                                                    {{ requirementExtraInfo(req) }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="inbox-reg-balance-row">
                                        <div class="inbox-reg-balance-item">
                                            <span class="inbox-reg-label">Tuition</span>
                                            <span
                                                class="inbox-reg-value"
                                                :class="{ 'inbox-reg-value--warning': selected.tuitionBalance > 0 }"
                                            >
                                                {{ (selected.tuitionBalance || 0) | currency }}
                                            </span>
                                        </div>
                                        <div class="inbox-reg-balance-item">
                                            <span class="inbox-reg-label">Transportation</span>
                                            <span
                                                class="inbox-reg-value"
                                                :class="{ 'inbox-reg-value--warning': selected.transportationBalance > 0 }"
                                            >
                                                {{ (selected.transportationBalance || 0) | currency }}
                                            </span>
                                        </div>
                                        <v-spacer></v-spacer>
                                        <v-btn
                                            small
                                            outline
                                            color="success"
                                            class="inbox-balance-btn"
                                            @click="openBalanceDialog"
                                        >
                                            <v-icon small>mdi-cash-multiple</v-icon>
                                            Balance details
                                        </v-btn>
                                    </div>
                                </div>

                                <div class="inbox-action-bar">
                                    <div class="inbox-action-row">
                                        <span class="inbox-assign-label">Assigned to:</span>
                                        <v-chip
                                            small
                                            text-color="white"
                                            :color="assignedChipColor(selected.assignedTo)"
                                        >
                                            {{ assignedChipLabel(selected.assignedTo) }}
                                        </v-chip>
                                        <v-btn
                                            v-if="selected.assignedTo !== user"
                                            small
                                            color="success"
                                            :loading="processing"
                                            @click="assignToMe(selected)"
                                        >
                                            {{ selected.assignedTo ? "Take over" : "Assign to me" }}
                                        </v-btn>
                                        <v-btn
                                            v-if="selected.assignedTo === user"
                                            small
                                            color="success"
                                            :loading="processing"
                                            @click="unassignUser(selected)"
                                        >
                                            Unassign
                                        </v-btn>
                                        <v-autocomplete
                                            v-model="assignToUser"
                                            class="inbox-assign-autocomplete"
                                            hide-details
                                            hide-no-data
                                            dense
                                            placeholder="Assign to user..."
                                            :items="users"
                                            :search-input.sync="loadUsers"
                                            :menu-props="userMenuProps"
                                            @focus="ensureUsersLoaded"
                                        ></v-autocomplete>
                                        <v-btn
                                            v-if="assignToUser"
                                            small
                                            color="success"
                                            :loading="processing"
                                            @click="assignToOther(selected)"
                                        >
                                            Assign
                                        </v-btn>
                                        <v-btn
                                            color="success"
                                            small
                                            @click="openCompleteDialog"
                                        >
                                            <v-icon small class="mr-1">mdi-check</v-icon>
                                            Mark done
                                        </v-btn>
                                        <v-btn
                                            v-if="!selected.needsAccount"
                                            color="success"
                                            small
                                            @click="openAccount(selected)"
                                        >
                                            <v-icon small class="mr-1">mdi-open-in-new</v-icon>
                                            Go to account
                                        </v-btn>
                                    </div>
                                </div>
                            </div>

                            <div class="inbox-detail-scroll">
                                <v-layout row align-center class="mb-2">
                                    <v-chip small text-color="white" :color="typeColor(selected.type)">
                                        <v-icon small color="white" class="mr-1">
                                            {{ typeIcon(selected.type) }}
                                        </v-icon>
                                        {{ typeLabel(selected.type) }}
                                    </v-chip>
                                    <v-spacer></v-spacer>
                                    <span class="caption grey--text">
                                        {{ selected.date | time }}
                                    </span>
                                </v-layout>
                                <div class="mb-1">
                                    <span class="font-weight-medium">From:</span>
                                    {{ formatPhone(selected.from) }}
                                </div>
                                <div class="mb-1">
                                    <span class="font-weight-medium">To:</span>
                                    {{ formatPhone(selected.to) }}
                                </div>
                                <div v-if="selected.subject" class="mb-3">
                                    <span class="font-weight-medium">Subject:</span>
                                    {{ selected.subject }}
                                </div>

                                <div
                                    v-if="selected.type === 'SMS'"
                                    class="inbox-body"
                                >
                                    {{ selected.text }}
                                </div>
                                <div
                                    v-else-if="selected.type === 'CALLBACK REQUEST'"
                                >
                                    <audio
                                        v-if="selected.recording"
                                        controls
                                        class="mb-2"
                                        :src="selected.recording"
                                    ></audio>
                                    <div v-if="selected.note">{{ selected.note }}</div>
                                </div>
                                <div v-else-if="selected.type === 'EMAIL'">
                                    <div v-if="selected.text" class="inbox-body mb-3">
                                        {{ selected.text }}
                                    </div>
                                    <template v-if="selected.hasAttachment || selected.attachmentCount">
                                        <div
                                            v-if="selected.needsAccount"
                                            class="inbox-link-required mb-2"
                                        >
                                            Link to an account before managing files.
                                        </div>
                                        <v-btn
                                            color="success"
                                            :disabled="!selected.emailID || selected.needsAccount"
                                            @click="openDocumentSplitter(selected)"
                                        >
                                            <v-icon small class="mr-1">mdi-file-multiple</v-icon>
                                            Manage files
                                        </v-btn>
                                    </template>
                                </div>
                                <div v-else-if="selected.type === 'FAX'">
                                    <div
                                        v-if="selected.needsAccount"
                                        class="inbox-link-required mb-2"
                                    >
                                        Link to an account before managing files.
                                    </div>
                                    <v-btn
                                        color="success"
                                        :disabled="!selected.faxID || selected.needsAccount"
                                        @click="openDocumentSplitter(selected)"
                                    >
                                        <v-icon small class="mr-1">mdi-file-multiple</v-icon>
                                        Manage files
                                    </v-btn>
                                </div>

                                <v-divider class="my-3"></v-divider>

                                <div
                                    v-if="selected.type === 'SMS'"
                                    class="mb-3"
                                >
                                    <v-btn
                                        color="success"
                                        :disabled="selected.needsAccount"
                                        @click="openAccount(selected)"
                                    >
                                        <v-icon small class="mr-1">mdi-reply</v-icon>
                                        Reply in Account View
                                    </v-btn>
                                </div>
                            </div>
                        </v-card>
                    </v-flex>
                </v-layout>
            </div>
        </v-card>

        <v-dialog v-model="balanceDialog" max-width="620">
            <v-card v-if="selected" class="pa-3">
                <v-card-title class="headline pl-0 pt-0">
                    Balance Details
                </v-card-title>
                <div class="inbox-balance-name mb-2">
                    {{ selected.name || selected.accountID }}
                    <span class="grey--text">&middot; {{ selected.accountID }}</span>
                </div>

                <v-switch
                    color="success"
                    class="mt-0 mb-1"
                    hide-details
                    v-model="showFurtherBilling"
                    @change="loadFurtherBilling"
                    label="Show Further Billing"
                ></v-switch>

                <v-layout row class="font-weight-medium">
                    <div style="width: 120px;">Date</div>
                    <div style="flex: 1 1 auto;">Description</div>
                    <div style="width: 90px;" class="text-xs-right">Amount</div>
                    <div style="width: 90px;" class="text-xs-right">Balance</div>
                </v-layout>
                <v-divider class="pb-1 mt-0"></v-divider>

                <div class="inbox-balance-transactions">
                    <div v-if="balanceDetailsLoading" class="text-xs-center pa-4">
                        <v-progress-circular indeterminate color="success" size="32"></v-progress-circular>
                    </div>
                    <template v-else>
                        <div v-for="(transaction, index) in balanceTransactions" :key="'t' + index">
                            <v-layout row>
                                <div style="width: 120px;">{{ transaction.date | date }}</div>
                                <div style="flex: 1 1 auto;">{{ transaction.description }}</div>
                                <div
                                    style="width: 90px;"
                                    class="text-xs-right font-weight-medium"
                                    v-if="transaction.txnType == 'D'"
                                >
                                    {{ transaction.amount | currency }}
                                </div>
                                <div
                                    style="width: 90px;"
                                    class="text-xs-right font-weight-medium"
                                    v-if="transaction.txnType == 'C'"
                                >
                                    ({{ transaction.amount | currency }})
                                </div>
                                <div style="width: 90px;" class="text-xs-right">
                                    {{ transaction.balance | currency }}
                                </div>
                            </v-layout>
                            <v-divider class="pb-1 mt-1"></v-divider>
                        </div>
                        <div v-for="(transaction, index) in futureBalanceTransactions" :key="'f' + index">
                            <v-layout row class="text-info">
                                <div style="width: 120px;">{{ transaction.date | date }}</div>
                                <div style="flex: 1 1 auto;">{{ transaction.description }}</div>
                                <div
                                    style="width: 90px;"
                                    class="text-xs-right font-weight-medium"
                                    v-if="transaction.type == 'transaction'"
                                >
                                    {{ transaction.amount | currency }}
                                </div>
                                <div
                                    style="width: 90px;"
                                    class="text-xs-right font-weight-medium"
                                    v-if="transaction.type == 'schedule'"
                                >
                                    ({{ transaction.amount | currency }})
                                </div>
                                <div style="width: 90px;" class="text-xs-right">
                                    {{ transaction.balance | currency }}
                                </div>
                            </v-layout>
                            <v-divider class="pb-1 mt-1"></v-divider>
                        </div>
                        <div
                            v-if="!balanceTransactions.length && !futureBalanceTransactions.length"
                            class="grey--text text-xs-center py-3"
                        >
                            No transaction history
                        </div>
                    </template>
                </div>

                <v-layout row class="mt-2 mr-1 title text-success" justify-space-between>
                    <div class="font-weight-medium">Total</div>
                    <div class="font-weight-medium">{{ balanceTransactionsTotal | currency }}</div>
                </v-layout>

                <v-card-actions class="mx-0 px-0 pb-0 mt-2">
                    <v-btn
                        color="success"
                        small
                        @click="balanceDialog = false; openAccount(selected)"
                    >
                        <v-icon small class="mr-1">mdi-open-in-new</v-icon>
                        Go to account
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="default" dark @click="balanceDialog = false">
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="bulkUpdateDialog" persistent max-width="640">
            <v-card class="inbox-complete-card">
                <div class="inbox-complete-header">
                    <v-icon color="white" class="mr-2">mdi-check-circle</v-icon>
                    <div class="inbox-complete-title">
                        Mark {{ selectedIds.length }}
                        {{ selectedIds.length === 1 ? "Communication" : "Communications" }}
                        Done
                    </div>
                </div>

                <div class="inbox-complete-body">
                    <div class="inbox-complete-list">
                        <div
                            v-for="item in selectedItemsList"
                            :key="item.communicationID"
                            class="inbox-complete-item"
                        >
                            <div
                                class="inbox-complete-item-icon"
                                :style="{ background: typeColor(item.type) }"
                            >
                                <v-icon small color="white">{{ typeIcon(item.type) }}</v-icon>
                            </div>
                            <div class="inbox-complete-item-body">
                                <div class="inbox-complete-item-header">
                                    <v-chip
                                        small
                                        text-color="white"
                                        :color="typeColor(item.type)"
                                        class="inbox-complete-item-chip"
                                    >
                                        {{ typeLabel(item.type) }}
                                    </v-chip>
                                    <span class="inbox-complete-item-name">
                                        {{ item.name || item.accountID || "Unknown account" }}
                                    </span>
                                    <span v-if="item.nameH" class="inbox-complete-item-name-he">
                                        {{ item.nameH }}
                                    </span>
                                    <span class="inbox-complete-item-date">
                                        {{ formatListDate(item.date) }} &middot; {{ formatListTime(item.date) }}
                                    </span>
                                </div>

                                <div
                                    v-if="!item.needsAccount"
                                    class="inbox-complete-item-account"
                                >
                                    <v-icon x-small color="#78909c" class="mr-1">mdi-account</v-icon>
                                    Account {{ item.accountID }}
                                    <span v-if="item.emailAddress"> &middot; {{ item.emailAddress }}</span>
                                    <span v-if="showFromContact(item)"> &middot; {{ formatPhone(item.from) }}</span>
                                </div>
                                <div v-else class="inbox-complete-item-account inbox-complete-item-account--missing">
                                    <v-icon x-small color="#fb8c00" class="mr-1">mdi-account-alert</v-icon>
                                    Unknown account
                                </div>

                                <fax-preview
                                    v-if="item.type === 'FAX' && item.faxID"
                                    :row="faxPreviewRow(item)"
                                    :hide-assign="true"
                                    class="inbox-complete-item-fax"
                                ></fax-preview>
                                <div v-else class="inbox-complete-item-message">
                                    {{ item.text || item.subject || "No message text" }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <v-textarea
                        v-model="bulkNote"
                        label="Note (required)"
                        hint="A note is required to mark a communication as done."
                        persistent-hint
                        outline
                        rows="3"
                        autofocus
                        class="mt-3 mb-0"
                    ></v-textarea>
                </div>

                <v-card-actions class="inbox-complete-actions">
                    <v-spacer></v-spacer>
                    <v-btn color="default" dark @click="cancelBulkUpdateDialog">
                        Cancel
                    </v-btn>
                    <v-btn
                        color="success"
                        :disabled="!bulkNote || !bulkNote.trim()"
                        :loading="bulkProcessing"
                        @click="confirmBulkUpdate"
                    >
                        Confirm
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="historyDialog" max-width="720">
            <v-card v-if="historyAccount" class="inbox-history-card">
                <div class="inbox-history-header">
                    <div>
                        <div class="inbox-history-title">
                            Communications
                            <span class="inbox-history-title-period">last 30 days</span>
                        </div>
                        <div class="inbox-history-account">
                            {{ historyAccount.name || historyAccount.accountID }}
                            <span>{{ historyAccount.accountID }}</span>
                        </div>
                    </div>
                    <v-btn icon small @click="historyDialog = false">
                        <v-icon small>mdi-close</v-icon>
                    </v-btn>
                </div>
                <div class="inbox-history-summary">
                    <span class="inbox-history-chip">{{ historyCounts.all }} total</span>
                    <span class="inbox-history-chip inbox-history-chip--sms">SMS {{ historyCounts.sms }}</span>
                    <span class="inbox-history-chip inbox-history-chip--callback">Callback {{ historyCounts.callback }}</span>
                    <span class="inbox-history-chip inbox-history-chip--email">Email {{ historyCounts.email }}</span>
                    <span class="inbox-history-chip inbox-history-chip--fax">Fax {{ historyCounts.fax }}</span>
                </div>
                <div class="inbox-history-list">
                    <div v-if="historyLoading" class="text-xs-center pa-4">
                        <v-progress-circular indeterminate color="success" size="32"></v-progress-circular>
                    </div>
                    <div
                        v-else-if="!historyItems.length"
                        class="grey--text text-xs-center py-4"
                    >
                        No incoming communications in the last 30 days
                    </div>
                    <template v-else>
                        <div
                            v-for="item in historyItems"
                            :key="'h' + item.communicationID"
                            class="inbox-history-row"
                            :class="{ 'inbox-history-row--open': item.expanded }"
                        >
                            <div
                                class="inbox-history-row-header"
                                @click="toggleHistoryItem(item)"
                            >
                                <div
                                    class="inbox-history-typeicon"
                                    :style="{ background: typeColor(item.type) }"
                                >
                                    <v-icon x-small color="white">
                                        {{ typeIcon(item.type) }}
                                    </v-icon>
                                </div>
                                <div class="inbox-history-body">
                                    <div class="inbox-history-top">
                                        <strong>{{ typeLabel(item.type) }}</strong>
                                        <span
                                            v-if="item.hasAttachment"
                                            class="inbox-history-attach"
                                            :title="item.attachmentCount + ' attachment(s)'"
                                        >
                                            <v-icon x-small color="#607d8b">mdi-paperclip</v-icon>
                                            {{ item.attachmentCount }}
                                        </span>
                                        <span class="inbox-history-date">{{ formatListDate(item.date) }} {{ formatListTime(item.date) }}</span>
                                    </div>
                                    <div class="inbox-history-preview">
                                        {{ historyPreview(item) }}
                                    </div>
                                    <div class="inbox-history-meta">
                                        <span v-if="item.from">From {{ formatPhone(item.from) }}</span>
                                        <v-chip
                                            small
                                            text-color="white"
                                            :color="item.status === 'COMPLETED' ? '#4caf50' : '#fdb400'"
                                        >
                                            {{ item.status || "—" }}
                                        </v-chip>
                                        <v-chip
                                            small
                                            text-color="white"
                                            :color="assignedChipColor(item.assignedTo)"
                                        >
                                            {{ assignedChipLabel(item.assignedTo) }}
                                        </v-chip>
                                    </div>
                                    <div v-if="item.note" class="inbox-history-note">
                                        Note: {{ item.note }}
                                    </div>
                                </div>
                                <v-icon class="inbox-history-chevron">
                                    {{ item.expanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                                </v-icon>
                            </div>

                            <div v-if="item.expanded" class="inbox-history-expand">
                                <div
                                    v-if="item.type === 'SMS'"
                                    class="inbox-history-expand-text"
                                >
                                    {{ item.text || "No message text" }}
                                </div>
                                <div v-else-if="item.type === 'CALLBACK REQUEST'">
                                    <audio
                                        v-if="item.recording"
                                        controls
                                        class="mb-2"
                                        :src="item.recording"
                                    ></audio>
                                    <div v-else class="grey--text">No recording</div>
                                </div>
                                <email-body
                                    v-else-if="item.type === 'EMAIL' && item.emailID"
                                    :row="emailPreviewRow(item)"
                                ></email-body>
                                <fax-preview
                                    v-else-if="item.type === 'FAX' && item.faxID"
                                    :row="faxPreviewRow(item)"
                                    :hide-assign="true"
                                ></fax-preview>
                            </div>
                        </div>
                    </template>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>
<script>
import * as firebase from "firebase/app";
import "firebase/database";
import moment from "moment";
import { formatPhoneNumber } from "../../utils/helpers.js";

export default {
    data() {
        return {
            loading: false,
            processing: false,
            items: [],
            counts: {
                all: 0,
                sms: 0,
                callback: 0,
                email: 0,
                fax: 0,
                needsAccount: 0,
                assignedToMe: 0,
                unassigned: 0,
            },
            filters: {
                search: "",
                assignedFilter: "mineAndUnassigned",
                assignedToUser: "",
                status: "PENDING",
                types: [],
                fromDate: "",
                toDate: "",
            },
            onlyNeedsAccount: false,
            sortBy: "newest",
            fromDateMenu: false,
            toDateMenu: false,
            typeOptions: [
                { label: "SMS", value: "SMS", countKey: "sms" },
                { label: "Callback", value: "CALLBACK REQUEST", countKey: "callback" },
                { label: "Email", value: "EMAIL", countKey: "email" },
                { label: "Fax", value: "FAX", countKey: "fax" },
            ],
            assignedFilterOptions: [
                { text: "My Queue (unassigned + mine)", value: "mineAndUnassigned" },
                { text: "Unassigned only", value: "unassigned" },
                { text: "Assigned to me", value: "mine" },
                { text: "Specific user...", value: "user" },
                { text: "Everyone", value: "all" },
            ],
            statusOptions: [
                { text: "Pending", value: "PENDING" },
                { text: "Completed", value: "COMPLETED" },
                { text: "All", value: "ALL" },
            ],
            sortOptions: [
                { text: "Newest first", value: "newest" },
                { text: "Oldest first", value: "oldest" },
                { text: "Type", value: "type" },
                { text: "Account name", value: "name" },
            ],
            selected: null,
            assignAccountID: "",
            errorMessage: "",
            user: "",
            users: [],
            loadUsers: null,
            assignToUser: "",
            selectedIds: [],
            balanceDialog: false,
            balanceDetailsLoading: false,
            balanceTransactions: [],
            futureBalanceTransactions: [],
            balanceTransactionsTotal: 0,
            showFurtherBilling: false,
            accountStudents: [],
            accountStudentsLoading: false,
            registrationRequirements: [],
            registrationLoading: false,
            bulkUpdateDialog: false,
            bulkNote: "",
            bulkProcessing: false,
            completingSingle: false,
            historyDialog: false,
            historyLoading: false,
            historyAccount: null,
            historyItems: [],
            historyCounts: {
                all: 0,
                sms: 0,
                callback: 0,
                email: 0,
                fax: 0,
            },
            firebaseNavRef: null,
            firebaseInboxRef: null,
            firebaseNavReady: false,
            firebaseInboxReady: false,
            lastInboxLoadAt: 0,
            pendingInboxReloadTimeout: null,
            userMenuProps: {
                offsetY: true,
                maxHeight: 280,
                contentClass: "inbox-user-menu",
            },
        };
    },
    computed: {
        isAdmin() {
            const isAdmin = this.$store.getters["authentication/isAdmin"];

            if (!isAdmin || isAdmin == "false") return false;

            return true;
        },
        visibleAssignedFilterOptions() {
            if (this.isAdmin) {
                return this.assignedFilterOptions;
            }

            return this.assignedFilterOptions.filter(
                (option) => option.value !== "user" && option.value !== "all",
            );
        },
        incompleteRequirements() {
            return this.registrationRequirements.filter(
                (req) => !req.isCompleted,
            );
        },
        isPartiallyRegistered() {
            if (!this.accountStudents || !this.accountStudents.length) {
                return false;
            }

            var hasRegistered = this.accountStudents.some(
                (s) => !!s.studentRegistered,
            );
            var hasUnregistered = this.accountStudents.some(
                (s) => !s.studentRegistered,
            );

            return hasRegistered && hasUnregistered;
        },
        selectedItemsList() {
            return this.items.filter(
                (item) => this.selectedIds.indexOf(item.communicationID) !== -1,
            );
        },
        allFilteredSelected() {
            return (
                this.filteredItems.length > 0 &&
                this.filteredItems.every(
                    (item) => this.selectedIds.indexOf(item.communicationID) !== -1,
                )
            );
        },
        filteredItems() {
            var search = (this.filters.search || "").toLowerCase().trim();
            var selectedTypes = this.filters.types;

            var list = this.items.filter((item) => {
                if (selectedTypes.length && selectedTypes.indexOf(item.type) === -1) {
                    return false;
                }

                if (this.onlyNeedsAccount && !item.needsAccount) {
                    return false;
                }

                if (!search) {
                    return true;
                }

                var haystack = [
                    item.name,
                    item.nameH,
                    item.accountID,
                    item.from,
                    item.to,
                    item.subject,
                    item.text,
                    item.assignedTo,
                ]
                    .join(" ")
                    .toLowerCase();

                return haystack.indexOf(search) !== -1;
            });

            return this.sortItems(list);
        },
    },
    watch: {
        async loadUsers(value) {
            if (!value || !value.trim()) {
                await this.loadAllUsers();
                return;
            }

            const response = await this.$http.get(
                `/odata/users?$count=true&$filter=(contains(userName, '${value.trim()}'))`,
            );

            this.users = response.value.map((u) => u.userName);
        },
    },
    created() {
        this.user = this.$store.state.authentication.user;
        this.loadPersistedFilters();
        this.loadAllUsers();

        if (
            !this.isAdmin &&
            (this.filters.assignedFilter === "user" ||
                this.filters.assignedFilter === "all")
        ) {
            this.filters.assignedFilter = "mineAndUnassigned";
        }

        this.loadInbox();
        this.firebaseSetup();
    },
    mounted() {
        this.setInboxFillHeight(true);
    },
    beforeDestroy() {
        this.setInboxFillHeight(false);
        if (this.pendingInboxReloadTimeout) {
            clearTimeout(this.pendingInboxReloadTimeout);
            this.pendingInboxReloadTimeout = null;
        }
        if (this.firebaseNavRef) {
            this.firebaseNavRef.off();
        }
        if (this.firebaseInboxRef) {
            this.firebaseInboxRef.off();
        }
    },
    methods: {
        async loadAllUsers() {
            try {
                const response = await this.$http.get(
                    "/odata/users?$count=true&$orderby=userName asc",
                );
                this.users = (response && response.value ? response.value : []).map(
                    function(u) {
                        return u.userName;
                    },
                );
            } catch (e) {
                this.users = [];
            }
        },
        ensureUsersLoaded() {
            if (!this.users.length) {
                this.loadAllUsers();
            }
        },
        setInboxFillHeight(enabled) {
            var coreView = document.getElementById("core-view");
            if (coreView) {
                if (enabled) {
                    coreView.classList.add("core-view--inbox");
                } else {
                    coreView.classList.remove("core-view--inbox");
                }
            }
            if (document.body) {
                if (enabled) {
                    document.body.classList.add("inbox-fill-height");
                } else {
                    document.body.classList.remove("inbox-fill-height");
                }
            }
        },
        async loadInbox() {
            if (this.pendingInboxReloadTimeout) {
                clearTimeout(this.pendingInboxReloadTimeout);
                this.pendingInboxReloadTimeout = null;
            }
            this.lastInboxLoadAt = Date.now();
            this.loading = true;
            this.errorMessage = "";
            this.persistFilters();

            var query =
                "api/communications/getInbox?type=&status=" +
                encodeURIComponent(this.filters.status) +
                "&user=" +
                encodeURIComponent(this.user) +
                "&assignedFilter=" +
                encodeURIComponent(this.filters.assignedFilter) +
                "&assignedToUser=" +
                encodeURIComponent(this.filters.assignedToUser || "") +
                "&fromDate=" +
                encodeURIComponent(this.filters.fromDate || "") +
                "&toDate=" +
                encodeURIComponent(this.filters.toDate || "");

            try {
                const response = await this.$http.get(query);

                this.items = (response && response.items) || [];
                this.counts = (response && response.counts) || this.counts;

                if (this.selected) {
                    var selectedId = this.selected.communicationID;
                    this.selected =
                        this.items.find(function(item) {
                            return item.communicationID === selectedId;
                        }) || null;
                }
            } catch (e) {
                this.errorMessage = "Could not load communications";
            }

            this.loading = false;
        },
        sortItems(list) {
            var sorted = list.slice();

            if (this.sortBy === "oldest") {
                sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
            } else if (this.sortBy === "type") {
                sorted.sort(
                    (a, b) =>
                        (a.type || "").localeCompare(b.type || "") ||
                        new Date(b.date) - new Date(a.date),
                );
            } else if (this.sortBy === "name") {
                sorted.sort((a, b) =>
                    (a.name || a.accountID || "").localeCompare(
                        b.name || b.accountID || "",
                    ),
                );
            } else {
                sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
            }

            return sorted;
        },
        setAssignedFilter(value) {
            this.filters.assignedFilter = value;
            this.loadInbox();
        },
        toggleType(value) {
            var index = this.filters.types.indexOf(value);
            if (index === -1) {
                this.filters.types.push(value);
            } else {
                this.filters.types.splice(index, 1);
            }
            this.persistFilters();
        },
        persistFilters() {
            try {
                localStorage.setItem(
                    "communicationsInboxFilters",
                    JSON.stringify({
                        assignedFilter: this.filters.assignedFilter,
                        status: this.filters.status,
                        types: this.filters.types,
                        sortBy: this.sortBy,
                    }),
                );
            } catch (e) {
                // ignore storage errors
            }
        },
        loadPersistedFilters() {
            try {
                var saved = localStorage.getItem("communicationsInboxFilters");
                if (!saved) return;

                var parsed = JSON.parse(saved);
                if (parsed.assignedFilter) {
                    this.filters.assignedFilter = parsed.assignedFilter;
                }
                if (parsed.status) {
                    this.filters.status = parsed.status;
                }
                if (Array.isArray(parsed.types)) {
                    this.filters.types = parsed.types;
                }
                if (parsed.sortBy) {
                    this.sortBy = parsed.sortBy;
                }
            } catch (e) {
                // ignore storage errors
            }
        },
        openCommunicationHistory(item) {
            if (!item || !item.accountID || item.needsAccount) {
                return;
            }

            this.historyAccount = item;
            this.historyDialog = true;
            this.loadCommunicationHistory(item.accountID);
        },
        async loadCommunicationHistory(accountID) {
            this.historyLoading = true;
            this.historyItems = [];
            this.historyCounts = {
                all: 0,
                sms: 0,
                callback: 0,
                email: 0,
                fax: 0,
            };

            try {
                var response = await this.$http.get(
                    "api/communications/getRecentForAccount?accountID=" +
                        encodeURIComponent(accountID),
                );
                if (
                    !this.historyAccount ||
                    this.historyAccount.accountID !== accountID
                ) {
                    return;
                }

                this.historyItems = ((response && response.items) || []).map(
                    function(item) {
                        item.expanded = false;
                        return item;
                    },
                );
                if (response && response.counts) {
                    this.historyCounts = response.counts;
                }
            } catch (e) {
                if (
                    this.historyAccount &&
                    this.historyAccount.accountID === accountID
                ) {
                    this.errorMessage =
                        "Could not load communications for this account.";
                }
            } finally {
                if (
                    !this.historyAccount ||
                    this.historyAccount.accountID === accountID
                ) {
                    this.historyLoading = false;
                }
            }
        },
        historyPreview(item) {
            var text = (item && (item.subject || item.text)) || "";
            if (text.length > 140) {
                return text.substring(0, 140) + "...";
            }
            return text || "—";
        },
        selectItem(item) {
            this.selected = item;
            this.assignAccountID = "";
            this.assignToUser = "";
            if (this.$refs.assignLookup && this.$refs.assignLookup.clear) {
                this.$refs.assignLookup.clear();
            }

            this.accountStudents = [];
            this.registrationRequirements = [];

            if (item && item.accountID && !item.needsAccount) {
                this.loadAccountStudents(item.accountID);
                this.loadRegistrationRequirements(item.accountID);
            }
        },
        async loadAccountStudents(accountID) {
            this.accountStudentsLoading = true;
            try {
                const students = await this.$http.get(
                    "api/accounts/getStudents?accountID=" +
                        accountID +
                        "&currentRegistrationOnly=true",
                );
                if (this.selected && this.selected.accountID === accountID) {
                    this.accountStudents = students || [];
                }
            } catch (e) {
                if (this.selected && this.selected.accountID === accountID) {
                    this.accountStudents = [];
                }
            }
            this.accountStudentsLoading = false;
        },
        async loadRegistrationRequirements(accountID) {
            this.registrationLoading = true;
            try {
                const requirements = await this.$http.get(
                    "api/accounts/getRegistrationRequirments?accountID=" + accountID,
                );
                if (this.selected && this.selected.accountID === accountID) {
                    this.registrationRequirements = requirements || [];
                }
            } catch (e) {
                if (this.selected && this.selected.accountID === accountID) {
                    this.registrationRequirements = [];
                }
            }
            this.registrationLoading = false;
        },
        typeLabel(type) {
            if (type === "CALLBACK REQUEST") return "Callback";
            if (type === "SMS") return "SMS";
            if (type === "EMAIL") return "Email";
            if (type === "FAX") return "Fax";
            return type;
        },
        typeIcon(type) {
            if (type === "SMS") return "mdi-message-text";
            if (type === "CALLBACK REQUEST") return "mdi-phone-return";
            if (type === "EMAIL") return "mdi-email";
            if (type === "FAX") return "mdi-fax";
            return "mdi-message";
        },
        typeColor(type) {
            if (type === "SMS") return "#5c6bc0";
            if (type === "CALLBACK REQUEST") return "#fdb400";
            if (type === "EMAIL") return "#26a69a";
            if (type === "FAX") return "#8d6e63";
            return "#607d8b";
        },
        regStatusColor(status) {
            var value = (status || "").toUpperCase();
            if (value.indexOf("PARTIAL") !== -1) {
                return "#fb8c00";
            }
            if (value.indexOf("REGISTERED") !== -1 && value.indexOf("NOT") === -1) {
                return "#4caf50";
            }
            if (value.indexOf("PENDING") !== -1) {
                return "#fdb400";
            }
            return "#e53935";
        },
        regStatusIcon(status) {
            var value = (status || "").toUpperCase();
            if (value.indexOf("PARTIAL") !== -1) {
                return "mdi-alert-circle-outline";
            }
            if (value.indexOf("REGISTERED") !== -1 && value.indexOf("NOT") === -1) {
                return "mdi-check-circle";
            }
            if (value.indexOf("PENDING") !== -1) {
                return "mdi-clock-outline";
            }
            return "mdi-close-circle";
        },
        assignedChipColor(assignedTo) {
            if (!assignedTo) return "#cfd8dc";
            return assignedTo === this.user ? "#4caf50" : "#78909c";
        },
        assignedChipLabel(assignedTo) {
            if (!assignedTo) return "Unassigned";
            return assignedTo === this.user ? "Me" : assignedTo;
        },
        formatListDate(value) {
            if (!value) return "";
            return moment(value).format("MM/DD/YYYY");
        },
        formatListTime(value) {
            if (!value) return "";
            return moment(value).format("h:mm A");
        },
        formatPhone(value) {
            if (!value) return "—";
            if (value.length === 10 || value.length === 11) {
                return formatPhoneNumber(
                    value.length === 11 ? value.substring(1) : value,
                );
            }
            return value;
        },
        previewText(item) {
            var text = item.text || item.subject || "";
            if (text.length > 90) {
                return text.substring(0, 90) + "...";
            }
            return text;
        },
        showFromContact(item) {
            if (!item || !item.from) return false;
            if (!item.emailAddress) return true;

            return (
                item.from.trim().toLowerCase() !==
                item.emailAddress.trim().toLowerCase()
            );
        },
        faxPreviewRow(item) {
            return {
                id: item.faxID,
                fileName: item.fileName,
                text: item.text,
                hasAttachment: item.hasAttachment,
                accountID: item.accountID,
            };
        },
        emailPreviewRow(item) {
            return {
                id: item.emailID,
                bodyHtml: item.bodyHtml,
                text: item.text,
                hasAttachments: item.hasAttachment,
                accountID: item.accountID,
            };
        },
        toggleHistoryItem(item) {
            item.expanded = !item.expanded;
        },
        requirementStudentName(req) {
            if (!req || !req.studentID) return "";

            var key = (req.studentID || "").trim();
            var student = this.accountStudents.find(function(s) {
                return ((s.studentID || "") + "").trim() === key;
            });

            return student ? student.engName || student.hebName || "" : "";
        },
        requirementExtraInfo(req) {
            if (!req) return "";

            var type = ((req.docTypeID || "") + " " + (req.reqType || "")).toUpperCase();
            var isVotingOrImm =
                type.indexOf("VOT") !== -1 || type.indexOf("IMM") !== -1;

            if (!isVotingOrImm) {
                return "";
            }

            if (req.description) {
                return req.description;
            }

            var student = this.accountStudents.find(function(s) {
                return (
                    req.studentID &&
                    ((s.studentID || "") + "").trim() === (req.studentID || "").trim()
                );
            });

            return (student && (student.missingImm || student.immRegStatus)) || "";
        },
        openDocumentSplitter(item) {
            if (!item || item.needsAccount || !item.accountID) {
                this.$notify({
                    group: "notification",
                    text: "Link this communication to an account before managing files.",
                    type: "error",
                });
                return;
            }

            var source = item.type === "FAX" ? "fax" : "email";
            var sourceId = item.type === "FAX" ? item.faxID : item.emailID;

            if (!sourceId) {
                return;
            }

            var query = {
                accountID: (item.accountID || "").trim(),
            };

            this.$router.push({
                name: "DocumentSplitter",
                params: {
                    source: source,
                    sourceId: sourceId,
                },
                query: query,
            });
        },
        openAccount(item) {
            if (!item.accountID || item.needsAccount) {
                return;
            }

            var query = {};
            if (item.type === "SMS") {
                query.goToMessageSection = true;
            } else if (item.type === "CALLBACK REQUEST") {
                query.goToCallbackSection = true;
            } else if (item.type === "EMAIL") {
                query.goToEmailsSection = true;
            } else if (item.type === "FAX") {
                query.goToFaxesSection = true;
            }

            this.$router.push({
                name: "Account View",
                params: { id: (item.accountID || "").trim() },
                query: query,
            });
        },
        async assignAccount() {
            if (!this.selected || !this.assignAccountID) {
                return;
            }

            this.processing = true;

            try {
                await this.$http.post("api/communications/assignAccount", {
                    type: this.selected.type,
                    emailID: this.selected.emailID,
                    faxID: this.selected.faxID,
                    smsID: this.selected.smsID,
                    callbackRequestID: this.selected.callbackRequestID,
                    accountID: this.assignAccountID,
                    user: this.user,
                });

                this.$notify({
                    group: "notification",
                    text: "Account assigned",
                    type: "success",
                });
                this.processing = false;
                this.loadInbox();
            } catch (e) {
                this.$notify({
                    group: "notification",
                    text: "Could not assign account",
                    type: "error",
                });
                this.processing = false;
            }
        },
        async assignToMe(item) {
            await this.assignUser(item, this.user);
        },
        async unassignUser(item) {
            await this.assignUser(item, "");
        },
        async assignToOther(item) {
            if (!this.assignToUser) {
                return;
            }
            await this.assignUser(item, this.assignToUser);
        },
        async assignUser(item, assignedTo) {
            if (!item) return;

            this.processing = true;

            try {
                await this.$http.post(
                    "api/communications/assignCommunicationUser",
                    {
                        communicationID: item.communicationID,
                        user: this.user,
                        assignedTo: assignedTo,
                    },
                );

                this.assignToUser = "";

                this.$notify({
                    group: "notification",
                    text: assignedTo
                        ? "Assigned to " + (assignedTo === this.user ? "you" : assignedTo)
                        : "Unassigned",
                    type: "success",
                });
                this.processing = false;
                this.loadInbox();
            } catch (e) {
                this.$notify({
                    group: "notification",
                    text:
                        (e && e.message) ||
                        "Could not update the assignment",
                    type: "error",
                });
                this.processing = false;
            }
        },
        toggleSelectAll(value) {
            if (value) {
                this.selectedIds = this.filteredItems.map(
                    (item) => item.communicationID,
                );
            } else {
                this.selectedIds = [];
            }
        },
        isRowSelected(item) {
            return this.selectedIds.indexOf(item.communicationID) !== -1;
        },
        toggleRowSelection(item) {
            var index = this.selectedIds.indexOf(item.communicationID);
            if (index === -1) {
                this.selectedIds.push(item.communicationID);
            } else {
                this.selectedIds.splice(index, 1);
            }
        },
        clearSelection() {
            this.selectedIds = [];
        },
        async bulkPickup() {
            await this.bulkAssign(this.user);
        },
        async bulkUnassign() {
            await this.bulkAssign("");
        },
        async bulkAssign(assignedTo) {
            if (!this.selectedIds.length) return;

            this.bulkProcessing = true;

            var ids = this.selectedIds.slice();
            var results = await Promise.all(
                ids.map((id) =>
                    this.$http
                        .post("api/communications/assignCommunicationUser", {
                            communicationID: id,
                            user: this.user,
                            assignedTo: assignedTo,
                        })
                        .then(() => true)
                        .catch(() => false),
                ),
            );

            var successCount = results.filter((result) => result).length;
            var failCount = results.length - successCount;

            this.$notify({
                group: "notification",
                text:
                    successCount +
                    (assignedTo ? " assigned to " + (assignedTo === this.user ? "you" : assignedTo) : " unassigned") +
                    (failCount
                        ? ", " + failCount + " skipped (already assigned to someone else)"
                        : ""),
                type: failCount ? "warning" : "success",
            });

            this.selectedIds = [];
            this.bulkProcessing = false;
            await this.loadInbox();
        },
        openBulkUpdateDialog() {
            if (!this.selectedIds.length) return;

            this.completingSingle = false;
            this.bulkNote = "";
            this.bulkUpdateDialog = true;
        },
        openCompleteDialog() {
            if (!this.selected) return;

            this.completingSingle = true;
            this.selectedIds = [this.selected.communicationID];
            this.bulkNote = "";
            this.bulkUpdateDialog = true;
        },
        cancelBulkUpdateDialog() {
            this.bulkUpdateDialog = false;

            if (this.completingSingle) {
                this.selectedIds = [];
                this.completingSingle = false;
            }
        },
        async confirmBulkUpdate() {
            if (!this.bulkNote || !this.bulkNote.trim()) {
                this.$notify({
                    group: "notification",
                    text:
                        "You can't mark as completed without entering a note.",
                    type: "error",
                });
                return;
            }

            this.bulkProcessing = true;

            var ids = this.selectedIds.slice();
            var results = await Promise.all(
                ids.map((id) =>
                    this.$http
                        .post("api/communications/updateStatus", {
                            communicationID: id,
                            status: "COMPLETED",
                            note: this.bulkNote,
                            user: this.user,
                        })
                        .then(() => true)
                        .catch(() => false),
                ),
            );

            var successCount = results.filter((result) => result).length;
            var failCount = results.length - successCount;

            this.$notify({
                group: "notification",
                text:
                    successCount +
                    " marked done" +
                    (failCount ? ", " + failCount + " failed" : ""),
                type: failCount ? "warning" : "success",
            });

            this.bulkUpdateDialog = false;
            this.selectedIds = [];
            this.completingSingle = false;
            if (this.selected && ids.indexOf(this.selected.communicationID) !== -1) {
                this.selected = null;
            }
            this.bulkProcessing = false;
            await this.loadInbox();
        },
        openBalanceDialog() {
            if (!this.selected) return;

            this.balanceDialog = true;
            this.showFurtherBilling = false;
            this.futureBalanceTransactions = [];
            this.loadBalanceTransactions();
        },
        async loadBalanceTransactions() {
            if (!this.selected || !this.selected.accountID) return;

            this.balanceDetailsLoading = true;
            try {
                const details = await this.$http.get(
                    "api/accounts/getTransactionDetails?accountID=" +
                        this.selected.accountID,
                );
                this.balanceTransactions = details || [];
                this.calculateBalanceTransactions();
            } catch (e) {
                this.balanceTransactions = [];
                this.balanceTransactionsTotal = 0;
            }
            this.balanceDetailsLoading = false;
        },
        calculateBalanceTransactions() {
            let totalBalance = 0;
            this.balanceTransactions = this.balanceTransactions.map((transaction) => {
                let balance = totalBalance;
                if (transaction.description !== "Scheduled Payment") {
                    balance =
                        totalBalance +
                        (transaction.txnType === "D"
                            ? transaction.amount
                            : -transaction.amount);
                }

                totalBalance = balance;
                return { ...transaction, balance: balance };
            });

            this.balanceTransactionsTotal = totalBalance;
        },
        async loadFurtherBilling(value) {
            if (!value) {
                this.futureBalanceTransactions = [];
                this.calculateBalanceTransactions();
                return;
            }

            if (!this.selected || !this.selected.accountID) return;

            let futureTransactions = await this.$http.get(
                "api/accounts/getFurtherTransactions?accountID=" +
                    this.selected.accountID,
            );

            let totalBalance = this.balanceTransactionsTotal;
            this.futureBalanceTransactions = (futureTransactions || []).map(
                (transaction) => {
                    const balance =
                        totalBalance +
                        (transaction.type === "transaction"
                            ? transaction.amount
                            : -transaction.amount);

                    totalBalance = balance;
                    return { ...transaction, balance: balance };
                },
            );

            this.balanceTransactionsTotal = totalBalance;
        },
        firebaseSetup() {
            const firebaseConfig = {
                apiKey: "AIzaSyCtYeCL8HkqlMKJh6g6jcDvoH60-woVbDE",
                authDomain: "utaw.firebaseapp.com",
                databaseURL: "https://utaw.firebaseio.com",
                projectId: "project-683582246459980907",
                storageBucket: "project-683582246459980907.appspot.com",
                messagingSenderId: "193425158146",
                appId: "1:193425158146:web:f1ae134a803fb75832371b",
            };

            if (!firebase.apps.length) {
                firebase.initializeApp(firebaseConfig);
            }

            var self = this;
            this.firebaseNavRef = firebase.database().ref("navigation");
            this.firebaseInboxRef = firebase
                .database()
                .ref("incoming-communications/inbox");

            this.firebaseNavRef.on("value", function() {
                if (!self.firebaseNavReady) {
                    self.firebaseNavReady = true;
                    return;
                }
                self.scheduleInboxReload();
            });
            this.firebaseInboxRef.on("value", function() {
                if (!self.firebaseInboxReady) {
                    self.firebaseInboxReady = true;
                    return;
                }
                self.scheduleInboxReload();
            });
        },
        scheduleInboxReload() {
            var minInterval = 15000;
            var remaining = minInterval - (Date.now() - this.lastInboxLoadAt);

            if (remaining <= 0) {
                this.loadInbox();
                return;
            }

            if (this.pendingInboxReloadTimeout) {
                return;
            }

            var self = this;
            this.pendingInboxReloadTimeout = setTimeout(function() {
                self.pendingInboxReloadTimeout = null;
                self.loadInbox();
            }, remaining);
        },
    },
};
</script>
<style>
body.inbox-fill-height .application,
body.inbox-fill-height .application--wrap,
body.inbox-fill-height .v-content,
body.inbox-fill-height .v-content__wrap,
body.inbox-fill-height #core-view.core-view--inbox {
    height: 100vh;
    max-height: 100vh;
    min-height: 0;
}

body.inbox-fill-height #core-view.core-view--inbox {
    padding-bottom: 0;
    overflow: hidden;
}

.communications-inbox {
    padding: 12px 16px;
    height: 100vh;
    margin-bottom: -100px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

body.inbox-fill-height .communications-inbox {
    height: 100%;
    margin-bottom: 0;
}

.inbox-root-card {
    flex: 1 1 auto;
    display: flex !important;
    flex-direction: column;
    min-height: 0;
    height: 100%;
    overflow: hidden;
}

.inbox-header {
    background-color: #4caf50;
    min-height: 0;
    flex: 0 0 auto;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    font-size: 16px;
}

.inbox-live {
    display: flex;
    align-items: center;
}

.inbox-live-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #c8e6c9;
    margin-right: 6px;
}

.inbox-refresh-btn {
    border-radius: 20px;
    font-weight: 500;
    letter-spacing: 0.3px;
    transition: background-color 0.15s ease;
}

.inbox-refresh-btn:hover {
    background-color: rgba(255, 255, 255, 0.15) !important;
}

.inbox-canvas {
    background: #fff;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    padding: 0;
}

.inbox-canvas > .inbox-chip-row.layout,
.inbox-canvas > .inbox-filter-bar,
.inbox-canvas > .v-alert,
.inbox-canvas > .v-progress-linear {
    flex: 0 0 auto !important;
}

.inbox-panel {
    background: #fff;
    border: none;
    border-bottom: 1px solid #dbe0e4;
    border-radius: 0;
    padding: 8px 12px;
}

.inbox-chip-row {
    row-gap: 6px;
}

.inbox-chip {
    cursor: pointer;
    margin: 2px 6px 2px 0;
}

.inbox-filter-bar {
    border: none !important;
    border-bottom: 1px solid #dbe0e4 !important;
    border-radius: 0;
    background: #fff;
    box-shadow: none !important;
}

.inbox-filter-field .v-input__slot {
    background: #f4f6f8 !important;
}

.inbox-filter-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}

.inbox-filter-field {
    flex: 1 1 170px;
    min-width: 150px;
}

.inbox-filter-field--search {
    flex: 2.2 1 260px;
    min-width: 220px;
}

.inbox-filter-field--assigned {
    flex: 1.9 1 240px;
    min-width: 230px;
}

.inbox-filter-field--assigned .v-select__selection {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: inline-block;
}

.inbox-filter-field--daterange {
    flex: 1.7 1 260px;
    min-width: 230px;
    display: flex;
    align-items: center;
    gap: 4px;
}

.inbox-filter-field--daterange .v-input {
    flex: 1 1 auto;
    min-width: 0;
}

.inbox-filter-daterange-sep {
    color: #b0bec5;
    flex: 0 0 auto;
}

.inbox-filter-field .v-input {
    border-radius: 6px;
}

.inbox-filter-field .v-input__slot {
    box-shadow: 0 0 0 1px #d6dbe0 inset !important;
}

.inbox-split {
    flex: 1 1 auto !important;
    min-height: 0;
    overflow: hidden;
    flex-wrap: nowrap !important;
    align-items: stretch !important;
    align-content: stretch !important;
    margin: 0 !important;
}

.inbox-split > .flex {
    padding: 0 !important;
}

.inbox-split-list,
.inbox-split-detail {
    display: flex;
    flex-direction: column;
    min-height: 0;
    max-height: 100%;
    align-self: stretch;
}

.inbox-split-list {
    padding-right: 0;
    height: 100%;
    border-right: 1px solid #dbe0e4;
}

.inbox-split-detail {
    padding-left: 0;
    height: 100%;
}

.inbox-detail-empty {
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: #fff;
    color: #78909c;
    border-radius: 0;
}

.inbox-list-card {
    border: none;
    border-radius: 0;
    background: #fff;
    overflow: hidden;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.inbox-row-grid {
    display: grid;
    grid-template-columns: 30px 30px 1fr 104px 40px 78px;
    column-gap: 8px;
    align-items: center;
}

.inbox-row-checkbox {
    display: flex;
    justify-content: center;
}

.inbox-row-checkbox .v-input--selection-controls {
    margin: 0;
    padding: 0;
}

.inbox-row-checkbox .v-input--selection-controls__ripple {
    left: -12px;
}

.inbox-bulk-bar {
    background: #e2f0e3;
    border: 1px solid #a5d6a7;
    border-radius: 6px;
    padding: 6px 10px;
    flex: 0 0 auto !important;
}

.inbox-bulk-bar > * {
    margin-right: 8px;
}

.inbox-bulk-count {
    font-size: 13px;
    font-weight: 600;
    color: #2e7d32;
}

.inbox-complete-card {
    overflow: hidden;
}

.inbox-complete-header {
    display: flex;
    align-items: center;
    padding: 14px 20px;
    background: #4caf50;
    color: #fff;
}

.inbox-complete-title {
    font-size: 18px;
    font-weight: 600;
}

.inbox-complete-body {
    padding: 16px 20px 4px;
}

.inbox-complete-list {
    max-height: 320px;
    overflow-y: auto;
    border: 1px solid #eceff1;
    border-radius: 6px;
}

.inbox-complete-item {
    display: flex;
    gap: 10px;
    padding: 12px 14px;
    border-bottom: 1px solid #eceff1;
}

.inbox-complete-item:last-child {
    border-bottom: none;
}

.inbox-complete-item-icon {
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
}

.inbox-complete-item-body {
    flex: 1 1 auto;
    min-width: 0;
}

.inbox-complete-item-header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
}

.inbox-complete-item-chip {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    height: 20px;
}

.inbox-complete-item-name {
    font-weight: 600;
    font-size: 14px;
    color: #263238;
    white-space: normal;
    word-break: break-word;
}

.inbox-complete-item-name-he {
    font-size: 13px;
    color: #78909c;
}

.inbox-complete-item-date {
    margin-left: auto;
    font-size: 11px;
    color: #90a4ae;
    white-space: nowrap;
}

.inbox-complete-item-account {
    margin-top: 4px;
    font-size: 12px;
    color: #607d8b;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}

.inbox-complete-item-account--missing {
    color: #fb8c00;
    font-weight: 600;
}

.inbox-complete-item-message {
    margin-top: 8px;
    padding: 8px 10px;
    background: #f6f7f8;
    border-radius: 6px;
    font-size: 13px;
    line-height: 1.4;
    color: #37474f;
    white-space: pre-wrap;
    word-break: break-word;
}

.inbox-complete-item-fax {
    margin-top: 8px;
    max-width: none;
    padding: 0;
}

.inbox-complete-item-fax .fax-preview-frame {
    height: 320px;
}

.inbox-complete-actions {
    margin: 0 20px 16px;
    padding: 0;
}

.inbox-row-grid--header {
    padding: 8px 12px;
    background: #dfe4e8;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: #546e7a;
    letter-spacing: 0.03em;
    border-bottom: 1px solid #cfd6dc;
}

.inbox-list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.inbox-item {
    cursor: pointer;
    border: none;
    border-bottom: 1px solid #eceff1;
    border-radius: 0;
    box-shadow: none;
    padding: 8px 12px;
}

.inbox-item:hover {
    background: #f1f4f6;
}

.inbox-item--active {
    background: #f1ffdc;
    box-shadow: inset 3px 0 0 #4caf50;
}

.inbox-row-type {
    display: flex;
    justify-content: center;
}

.inbox-row-account {
    min-width: 0;
}

.inbox-row-name {
    display: flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
}

.inbox-row-name-en {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.inbox-row-name-he {
    color: #78909c;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.inbox-row-preview {
    font-size: 12px;
    color: #90a4ae;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.inbox-row-assigned {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
}

.inbox-row-time {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    line-height: 1.25;
    text-align: right;
    white-space: nowrap;
}

.inbox-row-date {
    font-size: 12px;
    font-weight: 600;
    color: #37474f;
}

.inbox-row-clock {
    font-size: 11px;
    font-weight: 500;
    color: #546e7a;
}

.inbox-detail {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    border: none;
    border-radius: 0;
    box-shadow: none;
}

.inbox-detail-sticky {
    flex: 0 0 auto;
    max-height: 55vh;
    overflow-y: auto;
    border-bottom: 1px solid #cfd6dc;
    background: #f4f6f8;
}

.inbox-detail-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 16px;
    background: #fff;
}

.inbox-account-box {
    padding: 12px 16px 10px;
    background: #37474f;
    border-bottom: 3px solid #4caf50;
}

.inbox-account-box--missing {
    background: #5d4037;
    border-bottom-color: #ff9800;
}

.inbox-account-name {
    display: flex;
    align-items: center;
}

.inbox-account-name-en {
    font-size: 18px;
    font-weight: 600;
    color: #fff;
}

.inbox-account-name-he {
    margin-left: 8px;
    color: #cfd8dc;
}

.inbox-account-meta {
    font-size: 12px;
    color: #b0bec5;
    margin-top: 2px;
    display: flex;
    align-items: center;
}

.inbox-account-missing-text {
    color: #ffcc80;
    display: flex;
    align-items: center;
}

.inbox-link-box {
    margin: 10px 12px 4px;
    padding: 10px 12px 12px;
    background: #fff8e1;
    border: 1px solid #ffb74d;
    border-left: 4px solid #fb8c00;
    border-radius: 6px;
}

.inbox-link-title {
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #e65100;
    margin-bottom: 6px;
}

.inbox-link-row {
    display: flex;
    align-items: flex-end;
    gap: 10px;
}

.inbox-link-lookup {
    flex: 1 1 auto;
    min-width: 0;
}

.inbox-link-required {
    font-size: 13px;
    font-weight: 600;
    color: #e65100;
}

.inbox-row-attach {
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    font-size: 11px;
    font-weight: 600;
    color: #607d8b;
}

.inbox-reg-box {
    margin: 10px 12px 4px;
    padding: 10px 12px 12px;
    background: #fff;
    border: 1px solid #a5d6a7;
    border-left: 4px solid #4caf50;
    border-radius: 6px;
}

.inbox-reg-header {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
}

.inbox-reg-title {
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #2e7d32;
    margin-right: 8px;
}

.inbox-comm-count {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 600;
    color: #546e7a;
    white-space: nowrap;
    background: none;
    border: none;
    padding: 0;
}

.inbox-comm-count--link {
    cursor: pointer;
    color: #2e7d32;
    background: #e8f5e9;
    border: 1px solid #c8e6c9;
    border-radius: 14px;
    padding: 3px 8px 3px 6px;
    gap: 5px;
    text-decoration: none;
}

.inbox-comm-count--link:hover {
    background: #c8e6c9;
}

.inbox-comm-count-num {
    font-weight: 700;
}

.inbox-comm-count-period {
    color: #689f38;
    background: #fff;
    border-radius: 8px;
    padding: 0 5px;
    font-size: 10px;
    line-height: 16px;
}

.inbox-comm-count--grid {
    cursor: pointer;
    justify-content: center;
    min-width: 28px;
    height: 22px;
    padding: 0 6px;
    border-radius: 11px;
    background: #eceff1;
    color: #37474f;
}

.inbox-row-count {
    display: flex;
    justify-content: center;
}

.inbox-history-card {
    overflow: hidden;
}

.inbox-history-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 14px 12px 10px 16px;
    background: #4caf50;
    color: #fff;
}

.inbox-history-title {
    font-size: 18px;
    font-weight: 600;
    line-height: 1.2;
}

.inbox-history-title-period {
    margin-left: 6px;
    font-size: 12px;
    font-weight: 500;
    opacity: 0.85;
}

.inbox-history-account {
    margin-top: 4px;
    font-size: 13px;
    font-weight: 500;
}

.inbox-history-account span {
    margin-left: 6px;
    opacity: 0.8;
    font-weight: 400;
}

.inbox-history-header .v-btn {
    color: #fff;
    margin-top: -4px;
}

.inbox-history-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 10px 16px;
    background: #f4f6f8;
    border-bottom: 1px solid #eceff1;
}

.inbox-history-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 10px;
    background: #eceff1;
    color: #455a64;
    font-size: 11px;
    font-weight: 600;
}

.inbox-history-chip--sms {
    background: #e8eaf6;
    color: #3949ab;
}

.inbox-history-chip--callback {
    background: #fff8e1;
    color: #f9a825;
}

.inbox-history-chip--email {
    background: #e0f2f1;
    color: #00897b;
}

.inbox-history-chip--fax {
    background: #efebe9;
    color: #6d4c41;
}

.inbox-history-list {
    max-height: 65vh;
    overflow-y: auto;
}

.inbox-history-row {
    border-bottom: 1px solid #eceff1;
}

.inbox-history-row:last-child {
    border-bottom: none;
}

.inbox-history-row--open {
    background: #fafbfb;
}

.inbox-history-row-header {
    display: flex;
    align-items: flex-start;
    padding: 10px 16px;
    cursor: pointer;
}

.inbox-history-row-header:hover {
    background: #f5f7f8;
}

.inbox-history-chevron {
    flex: 0 0 auto;
    margin-top: 1px;
    color: #b0bec5 !important;
}

.inbox-history-attach {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    color: #607d8b;
}

.inbox-history-expand {
    padding: 0 16px 14px 52px;
}

.inbox-history-expand-text {
    font-size: 13px;
    line-height: 1.45;
    color: #37474f;
    white-space: pre-wrap;
    word-break: break-word;
    background: #f6f7f8;
    border-radius: 6px;
    padding: 8px 10px;
}

.inbox-history-expand .fax-preview,
.inbox-history-expand .email-body {
    max-width: none;
    padding: 0;
}

.inbox-history-expand .fax-preview-frame,
.inbox-history-expand .email-html-frame {
    height: 360px;
}

.inbox-history-typeicon {
    flex: 0 0 auto;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    margin-top: 1px;
}

.inbox-history-body {
    min-width: 0;
    flex: 1 1 auto;
}

.inbox-history-top {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
}

.inbox-history-date {
    color: #78909c;
    font-size: 12px;
    white-space: nowrap;
    margin-left: auto;
}

.inbox-history-preview {
    margin-top: 2px;
    font-size: 12px;
    color: #546e7a;
}

.inbox-history-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    font-size: 11px;
    color: #78909c;
}

.inbox-history-note {
    margin-top: 6px;
    font-size: 12px;
    color: #37474f;
    background: #f7f9fa;
    border-radius: 4px;
    padding: 4px 8px;
}

.inbox-balance-btn .v-btn__content {
    align-items: center;
}

.inbox-balance-btn .v-icon {
    font-size: 16px !important;
    height: 16px;
    width: 16px;
    line-height: 16px;
    margin-right: 6px;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
}

.inbox-reg-students {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 8px;
}

.inbox-reg-loading {
    display: flex;
    align-items: center;
    font-size: 12px;
    color: #78909c;
}

.inbox-student-chip {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    font-weight: 500;
    color: #37474f;
    background: #f1f8f2;
    border: 1px solid #c8e6c9;
    border-radius: 12px;
    padding: 3px 10px 3px 6px;
}

.inbox-student-chip--warning {
    background: #fff3e0;
    border-color: #ffcc80;
}

.inbox-student-class {
    color: #78909c;
    margin-left: 3px;
    font-weight: 400;
}

.inbox-reg-missing {
    margin-bottom: 8px;
}

.inbox-reg-missing-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 130px;
    overflow-y: auto;
    padding-right: 4px;
}

.inbox-reg-missing-item {
    background: #ffebee;
    border-radius: 4px;
    padding: 4px 8px;
}

.inbox-reg-missing-title {
    font-size: 12px;
    font-weight: 600;
    color: #c62828;
}

.inbox-reg-missing-student {
    margin-right: 4px;
}

.inbox-reg-missing-desc {
    font-size: 11px;
    font-weight: 400;
    color: #8d4b4b;
    line-height: 1.3;
    margin-top: 2px;
}

.inbox-reg-balance-row {
    display: flex;
    align-items: center;
    gap: 20px;
    padding-top: 8px;
    border-top: 1px solid #eceff1;
}

.inbox-reg-balance-item {
    display: flex;
    flex-direction: column;
}

.inbox-balance-name {
    font-size: 14px;
    font-weight: 600;
    color: #37474f;
}

.inbox-balance-transactions {
    max-height: 340px;
    overflow-y: auto;
}

.inbox-reg-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #78909c;
    margin-bottom: 4px;
}

.inbox-reg-value {
    font-size: 13px;
    font-weight: 600;
    color: #37474f;
    line-height: 1.3;
}

.inbox-reg-value--warning {
    color: #c62828;
}

.inbox-action-bar {
    padding: 10px 16px 12px;
    overflow: visible;
}

.inbox-action-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
    overflow: visible;
}

.inbox-user-menu {
    z-index: 300 !important;
}

.inbox-assign-label {
    font-size: 13px;
    font-weight: 500;
    color: #546e7a;
    flex: 0 0 auto;
}

.inbox-assign-autocomplete {
    max-width: 180px;
    min-width: 140px;
    flex: 1 1 140px;
}

.inbox-body {
    white-space: pre-line;
    line-height: 1.45;
}
</style>
